"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, Check, Loader2, X, User, Mail, Building2, Text } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
interface AccessRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function AccessRequestModal({
  isOpen,
  onClose
}: AccessRequestModalProps) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [department, setDepartment] = useState("");
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // NEXT_PUBLIC_BROWSER_AI_SERVICE_URL overrides NEXT_PUBLIC_API_URL for browser-side
  // fetches only — see the comment in lib/api-client.ts for why these differ locally.
  const API_URL = process.env.NEXT_PUBLIC_BROWSER_AI_SERVICE_URL || process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate domain
    const domain = email.split("@")[1]?.toLowerCase();
    if (!["3phalliance.com", "hrcrs.com"].includes(domain)) {
      setError("Only @3phalliance.com and @hrcrs.com emails are allowed");
      setIsLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/auth/request-access`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          full_name: fullName.trim(),
          department: department.trim() || null,
          reason: reason.trim() || null
        })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to submit request");
      }
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit request");
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    if (!isLoading) {
      setEmail("");
      setFullName("");
      setDepartment("");
      setReason("");
      setError(null);
      setIsSuccess(false);
      onClose();
    }
  };
  return <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <AnimatePresence mode="wait">
          {isSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="py-6 text-center">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: "spring",
            delay: 0.1
          }} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <ArrowUp className="size-8 text-primary" />
              </motion.div>

              <motion.h3 initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg font-semibold text-foreground mb-2">
                Request Submitted
              </motion.h3>

              <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-muted-foreground text-sm mb-6">
                Your access request has been sent to the administrator.
                You&apos;ll receive an email once your request is reviewed.
              </motion.p>

              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }}>
                <Button onClick={handleClose} className="min-w-[120px]">
                  <Check className="size-4" />
                  Done
                </Button>
              </motion.div>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }}>
              <DialogHeader>
                <DialogTitle>Request Access</DialogTitle>
                <DialogDescription>
                  Submit a request to access CRUCIBLE. An administrator will review
                  your request and contact you.
                </DialogDescription>
              </DialogHeader>

              {/* Error message */}
              <AnimatePresence mode="wait">
                {error && <motion.div initial={{
              opacity: 0,
              y: -10,
              height: 0
            }} animate={{
              opacity: 1,
              y: 0,
              height: "auto"
            }} exit={{
              opacity: 0,
              y: -10,
              height: 0
            }} className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
                    <X className="size-4 text-destructive shrink-0 mt-0.5" />
                    <p className="text-destructive text-sm">{error}</p>
                  </motion.div>}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                {/* Full Name */}
                <div className="space-y-2">
                  <label htmlFor="fullName" className="text-sm font-medium text-foreground">
                    Full Name <span className="text-destructive">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input id="fullName" type="text" value={fullName} onChange={e_0 => setFullName(e_0.target.value)} placeholder="John Doe" required className="pl-10" />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label htmlFor="requestEmail" className="text-sm font-medium text-foreground">
                    Work Email <span className="text-destructive">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input id="requestEmail" type="email" value={email} onChange={e_1 => setEmail(e_1.target.value)} placeholder="you@3phalliance.com" required className="pl-10" />
                  </div>
                </div>

                {/* Department */}
                <div className="space-y-2">
                  <label htmlFor="department" className="text-sm font-medium text-foreground">
                    Department
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input id="department" type="text" value={department} onChange={e_2 => setDepartment(e_2.target.value)} placeholder="e.g., Clinical Operations" className="pl-10" />
                  </div>
                </div>

                {/* Reason */}
                <div className="space-y-2">
                  <label htmlFor="reason" className="text-sm font-medium text-foreground">
                    Why do you need access?
                  </label>
                  <div className="relative">
                    <Text className="absolute left-3 top-3 size-4 text-muted-foreground" />
                    <Textarea id="reason" value={reason} onChange={e_3 => setReason(e_3.target.value)} placeholder="Briefly describe your use case..." rows={3} className="pl-10 resize-none" />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isLoading || !email || !fullName} className="flex-1">
                    {isLoading ? <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: "linear"
                }}>
                        <Loader2 className="size-4" />
                      </motion.div> : <ArrowUp className="size-4" />}
                    {isLoading ? "Submitting..." : "Submit Request"}
                  </Button>
                </div>

                <p className="text-center text-muted-foreground text-xs">
                  Requests are reviewed within 24 hours
                </p>
              </form>
            </motion.div>}
        </AnimatePresence>
      </DialogContent>
    </Dialog>;
}